import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  actualTheme: 'light' | 'dark';
}

interface ThemeActions {
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  cycleTheme: () => void;
  initializeTheme: () => void;
  getSystemTheme: () => 'light' | 'dark';
}

export const useThemeStore = create<ThemeState & ThemeActions>()(
  persist(
    (set, get) => ({
      theme: 'system',
      actualTheme: 'light',

      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },

      toggleTheme: () => {
        const currentTheme = get().theme;
        if (currentTheme === 'system') {
          set({ theme: 'light' });
          applyTheme('light');
        } else if (currentTheme === 'light') {
          set({ theme: 'dark' });
          applyTheme('dark');
        } else {
          set({ theme: 'light' });
          applyTheme('light');
        }
      },

      cycleTheme: () => {
        const currentTheme = get().theme;
        const themes: Theme[] = ['light', 'dark', 'system'];
        const currentIndex = themes.indexOf(currentTheme);
        const nextIndex = (currentIndex + 1) % themes.length;
        const nextTheme = themes[nextIndex];
        set({ theme: nextTheme });
        applyTheme(nextTheme);
      },

      initializeTheme: () => {
        const theme = get().theme;
        applyTheme(theme);
      },

      getSystemTheme: () => {
        if (typeof window !== 'undefined') {
          return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return 'light';
      },
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({
        theme: state.theme,
      }),
    }
  )
);

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  
  if (theme === 'system') {
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    root.classList.toggle('dark', systemTheme === 'dark');
    useThemeStore.setState({ actualTheme: systemTheme });
  } else {
    root.classList.toggle('dark', theme === 'dark');
    useThemeStore.setState({ actualTheme: theme });
  }
  
  // Atualizar meta theme-color para PWA
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    metaThemeColor.setAttribute('content', isDark ? '#0f172a' : '#3b82f6');
  }
}

// Listen for system theme changes
if (typeof window !== 'undefined') {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    const { theme } = useThemeStore.getState();
    if (theme === 'system') {
      applyTheme('system');
    }
  });
}
